import { api_base_url, front_base_url } from '../../constants';
import { API_INSTANCE } from '../../utils/service-http';
import { getAppLocale } from '../../utils/app-locale';
import { createAction, createReducer } from 'redux-act';
import Configuration from '../../models/Configuration';
import { downloadURI } from '../../utils/download';
import { appConfiguration } from '../../utils/app-starter';

const documentationCache = {};

const datasheetCache = {};

/**
 * Reducer and action used to retrieve documentation from a configuration.
 */
const DEFAULT_STATE = { documentTypes: [], cadUrls: {}, isLoading: false, error: null };

export const getDocumentationStarted = createAction('GET_DOCUMENTATION_STARTED');
export const getDocumentationSuccess = createAction('GET_DOCUMENTATION_SUCCESS');
export const getDocumentationFailed = createAction('GET_DOCUMENTATION_FAILED');

const DEFAULT_CAD_STATE = { isLoading: false, error: null };

export const getCADStarted = createAction('GET_CAD_STARTED');
export const getCADSuccess = createAction('GET_CAD_SUCCESS');
export const getCADFailed = createAction('GET_CAD_FAILED');

const DEFAULT_DATASHEET_STATE = { documentTypes: [], isLoading: false, error: null };

export const getDatasheetStarted = createAction('GET_DATASHEET_STARTED');
export const getDatasheetSuccess = createAction('GET_DATASHEET_SUCCESS');
export const getDatasheetFailed = createAction('GET_DATASHEET_FAILED');

export const documentationReducer = createReducer(function (on, off) {
  on(getDocumentationStarted, (state, payload) => {
    return {
      documentTypes: [],
      cadUrls: {},
      isLoading: true,
      error: null,
    }
  });
  on(getDocumentationSuccess, (state, payload) => {
    return {
      documentTypes: payload.documentTypes,
      cadUrls: payload.cadUrls,
      isLoading: false,
      error: null,
    }
  });
  on(getDocumentationFailed, (state, payload) => {
    return {
      documentTypes: [],
      cadUrls: {},
      isLoading: false,
      error: null,
    }
  });
}, DEFAULT_STATE);

export const cadReducer = createReducer(function (on, off) {
  on(getCADStarted, (state, payload) => {
    return {
      isLoading: true,
      error: null,
    }
  });
  on(getCADSuccess, (state, payload) => {
    return {
      isLoading: false,
      error: null,
    }
  });
  on(getCADFailed, (state, payload) => {
    return {
      isLoading: false,
      error: null,
    }
  });
}, DEFAULT_CAD_STATE);

export const datasheetReducer = createReducer(function (on, off) {
  on(getDatasheetStarted, (state, payload) => {
    return {
      documentTypes: [],
      isLoading: true,
      error: null,
    }
  });
  on(getDatasheetSuccess, (state, payload) => {
    return {
      documentTypes: payload.documentTypes,
      isLoading: false,
      error: null,
    }
  });
  on(getDatasheetFailed, (state, payload) => {
    return {
      documentTypes: [],
      isLoading: false,
      error: null,
    }
  });
}, DEFAULT_DATASHEET_STATE);

export function getDocumentation(configuration) {
  const configObject = new Configuration(configuration);
  const referenceIds = configObject.extractDistinctReferencesForDocumentation().sort();
  const mainOverrideString = appConfiguration.main ? `&main=${appConfiguration.main}` : '';
  return (dispatch) => {
    const cachedDocumentation = documentationCache[referenceIds.toString()];
    if (cachedDocumentation) {
      dispatch(getDocumentationSuccess({
        cadUrls: cachedDocumentation.cadUrls,
        documentTypes: buildDocumentTypes(cachedDocumentation.documentTypes)
      }));
      return cachedDocumentation;
    }
    dispatch(getDocumentationStarted());
    return API_INSTANCE
      .post(`${api_base_url}/documentation?locale=${getAppLocale()}${mainOverrideString}`, { referenceIds }).then((success) => {
        const documentTypes = success.data.payload.documentTypes;
        documentationCache[referenceIds.toString()] = success.data.payload;
        dispatch(getDocumentationSuccess({
          cadUrls: success.data.payload.cadUrls,
          documentTypes: buildDocumentTypes(documentTypes)
        }));
        return success.data.payload;
      }).catch((error) => {
        dispatch(getDocumentationFailed());
        throw error;
      });
  }
}

export function downloadDocuments(documents, configurationName, documentName) {
  const params = composeDownloadParams(documents);
  const archiveName = `${documentName} - ${configurationName}.zip`;

  return `https://download.schneider-electric.com/files?p_Archive_Name=${archiveName}&${params}&p_Scope_Independant=false`;
}

const sum = list => list.reduce((a, c) => a + c, 0);

function groupMapper(groupName, group) {
  const sizeValue = Math.round(sum(group.map(g => g.size * 0.000001)));
  const documentGroup = new Map();
  documentGroup.set('name', groupName);
  documentGroup.set('group', group);
  if (sizeValue > 0) {
    documentGroup.set('fileSize', `${sizeValue} MB`);
  }
  return documentGroup;

}

function buildDocumentTypes(data) {
  return Object.keys(data).map(groupName => groupMapper(groupName, data[groupName]))
}

const reduceDocumentsList = documents => {
  return Object.values(documents).reduce((a, c) => [...a, ...c], []);
};

const composeDownloadParams = documents => {
  let result = null;
  try {
    const parameters = [];
    const documentList = !documents.length ? reduceDocumentsList(documents) : documents;

    function addType(array, paramName) {
      const str = array.filter(v => v).join(',');
      if (str) {
        parameters.push(`${paramName}=${str}`);
      }
    }

    addType(documentList.map(d => encodeURIComponent(d.documentReference).replace(/%20/g, '+')), 'p_Doc_Ref');
    addType(documentList.filter(d => d.documentType === 'Product Data Sheet').map(d => d.fileName), 'p_File_Name');

    result = parameters.join('&');
  } catch (error) {
    console.log(error);
  }
  return result;
};

export const downloadCAD = (configuration, name) => {
  const mainOverrideString = appConfiguration.main ? `&main=${appConfiguration.main}` : '';
  return dispatch => {
    const configObject = new Configuration(configuration);
    const referenceIds = configObject.extractDistinctReferencesForDocumentation().sort();
    dispatch(getCADStarted());
    return API_INSTANCE
      .post(`${api_base_url}/documentation-cad?fileName=${configObject.id}&locale=${getAppLocale()}${mainOverrideString}`, { referenceIds }).then((success) => {
        const URL = `${front_base_url}/${success.data.payload}`;
        downloadURI(URL, `${name}`);
        dispatch(getCADSuccess());
      }).catch((error) => {
        dispatch(getCADFailed());
        throw error;
      });
  }
}

export const getDatasheet = (configuration) => {
  const configObject = new Configuration(configuration);
  const referenceIds = configObject.extractDistinctReferencesForDocumentation().sort();
  const mainOverrideString = appConfiguration.main ? `&main=${appConfiguration.main}` : '';
  return (dispatch) => {
    const cachedDocumentation = datasheetCache[referenceIds.toString()];
    if (cachedDocumentation) {
      dispatch(getDocumentationSuccess({
        documentTypes: buildDocumentTypes(cachedDocumentation.documentTypes)
      }));
      return cachedDocumentation;
    }
    dispatch(getDatasheetStarted());
    return API_INSTANCE
      .post(`${api_base_url}/documentation/datasheet?locale=${getAppLocale()}${mainOverrideString}`, { referenceIds }).then((success) => {
        const documentTypes = success.data.payload.documentTypes;
        datasheetCache[referenceIds.toString()] = success.data.payload;
        dispatch(getDatasheetSuccess({
          documentTypes: buildDocumentTypes(documentTypes)
        }));
        return success.data.payload;
      }).catch((error) => {
        dispatch(getDatasheetFailed());
        throw error;
      });
  }

}
